import { actionCardChipTitle } from '@littlebird/action-cards';
import { buildMeetingNotesSendContext, useStores } from '@littlebird/sdk';
import { reaction } from 'mobx';
import { useCallback, useLayoutEffect, useRef } from 'react';

import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useViewportSize } from '@/hooks/useViewportSize';
import { uiStore } from '@/stores/ui.store';

import { useContextChatHost } from './context-chat.host';
import { contextChatStore } from './context-chat.state';
import { computeDockMetrics } from './context-chat.utils';

import type {
  ChatTarget,
  MeetingChatTarget,
  SessionContext,
} from './context-chat.types';

const DOCK_INSET_SETTLE_MS = 100;

export function useContextChatTarget(
  target: ChatTarget,
  enabled: boolean,
  {
    dockOnly = false,
    embedded = false,
  }: { dockOnly?: boolean; embedded?: boolean } = {}
) {
  useLayoutEffect(() => {
    if (!enabled) {
      contextChatStore.close();
      contextChatStore.setCurrentTarget(undefined);
      return;
    }

    contextChatStore.setDockOnly(dockOnly);
    contextChatStore.setEmbedded(embedded);
    return () => {
      contextChatStore.collapseForNavigation();
      contextChatStore.setCurrentTarget(undefined);
      contextChatStore.setDockOnly(false);
      contextChatStore.setEmbedded(false);
    };
  }, [enabled, dockOnly, embedded]);

  useLayoutEffect(() => {
    if (enabled) contextChatStore.setCurrentTarget(target);
  }, [enabled, target]);
}

export function useChatDockInset(isOverlayExpanded: () => boolean) {
  const dockRef = useRef<HTMLDivElement>(null);

  const setDockInset = useDebouncedCallback(() => {
    const el = dockRef.current;
    const scope = el?.closest<HTMLElement>('[data-chat-dock-scope]');
    if (!el || !scope || isOverlayExpanded()) return;
    const inset =
      scope.getBoundingClientRect().bottom - el.getBoundingClientRect().top;
    scope.style.setProperty('--chat-dock-inset', `${inset}px`);
  }, DOCK_INSET_SETTLE_MS);

  useLayoutEffect(() => {
    const el = dockRef.current;
    if (!el) return;
    el.closest<HTMLElement>('[data-chat-dock-scope]')?.style.setProperty(
      '--chat-dock-inset',
      '0px'
    );
    setDockInset();
    setDockInset.flush();
    const ro = new ResizeObserver(() => setDockInset());
    ro.observe(el);
    const disposeReaction = reaction(isOverlayExpanded, (expanded) => {
      if (!expanded) setDockInset();
    });
    return () => {
      disposeReaction();
      ro.disconnect();
    };
  }, [isOverlayExpanded, setDockInset]);

  return dockRef;
}

export function useDockMetrics() {
  // React Compiler would memoize this on viewportWidth and miss MobX changes
  'use no memo';
  const { width: viewportWidth } = useViewportSize();
  const leftSidebar = uiStore.isSidebarHidden ? 0 : uiStore.sidebarWidth;
  return computeDockMetrics({
    viewportWidth,
    leftSidebar,
    sidebarWidth: contextChatStore.sidebarWidth,
  });
}

export function useAskContextChatSelection(
  context: MeetingChatTarget | undefined
): (selection: { text: string }) => void {
  const meetingId = context?.meetingId;

  return useCallback(
    ({ text }) => {
      if (!meetingId) return;
      contextChatStore.askAboutSelection({ type: 'meeting', meetingId }, text);
    },
    [meetingId]
  );
}

export function useSessionTitle(context: SessionContext): string | undefined {
  const { actionCards, meetings } = useStores();

  if (context.type === 'meeting') {
    return meetings.get(context.meetingId)?.name?.trim() || undefined;
  }

  if (context.type === 'action-card') {
    const card = actionCards.get(context.cardId);
    return (card && actionCardChipTitle(card)) || undefined;
  }

  return undefined;
}

export function useSessionSendContextBuilder(
  context: SessionContext
): (() => string | undefined) | undefined {
  const host = useContextChatHost();

  return useCallback(() => {
    if (context.type !== 'meeting') return undefined;

    // Backend injects meeting details/transcript for chats with
    // `meeting_id`, so only send client-only notes here.
    return buildMeetingNotesSendContext(
      host.getMeetingNotes(context.meetingId)
    );
  }, [context, host]);
}
